import { View } from "@pimo/pimo-app-builder";
import { DragAndDropLayout } from "@pimo/pimo-components/src/lib/layouts/drag-and-drop-layout/drag-and-drop-layout";
import {
  PMBarChartCard,
  PMDonutChartCard,
} from "@pimo/pimo-components/src/lib/pm-components";
import { ReportingItem } from "vmd-types";

import { TestAppState } from "../../../app";
import { DONUT_CARD_LAYOUT, DONUT_CHART_COLORS } from "../../../constants";
import {
  DashboardChartEntry,
  getAllReportingItems,
  withTotalInTitle,
} from "../../../helpers/pages-helpers/dashboard-plugin-helpers";

const getLifeCycleData = (
  items: ReportingItem[]
): { entries: DashboardChartEntry[] } => {
  const counts = new Map<string, number>();

  for (const item of items) {
    const lifeCycle = item.basicItem?.lifeCycle;
    if (!lifeCycle) {
      continue;
    }
    counts.set(lifeCycle, (counts.get(lifeCycle) ?? 0) + 1);
  }

  const entries = Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([label, count], index) => ({
      label,
      count,
      color: DONUT_CHART_COLORS[index % DONUT_CHART_COLORS.length],
    }));

  return { entries };
};

/**
 * The bar and donut chart pair showing systems per life cycle (#2222) —
 * hand-duplicated copies of the same data before this split.
 */
export function addLifeCycleChartsCard(
  view: View<TestAppState, DragAndDropLayout>
): void {
  const lifeCycleBarChartComponent = view.addComponent({
    component: PMBarChartCard,
    layoutProps: {
      componentId: "lifeCycleDashboardBarChartComponent",
      labels: ["barChart", "chart", "lifeCycle"],
      description: "Shows systems grouped by life cycle across all projects.",
      title: "Systems per Life Cycle",
    },
  });

  lifeCycleBarChartComponent.mapState(({ dashboard }) => {
    const allItems = getAllReportingItems(dashboard);
    const { entries } = getLifeCycleData(allItems);

    return {
      title: "Systems per Life Cycle",
      categories: entries.map((entry) => entry.label),
      data: entries.map((entry) => entry.count),
      chartType: 1 as const,
      xLabel: "Systems",
      yLabel: "Life Cycle",
      colors: entries.map((entry) => entry.color),
      fixedHeight: "320px",
    };
  });

  const lifeCycleDonutChartComponent = view.addComponent({
    component: PMDonutChartCard,
    layoutProps: {
      componentId: "lifeCycleDashboardDonutChartComponent",
      labels: ["donutChart", "chart", "lifeCycle"],
      description: "Shows systems grouped by life cycle across all projects.",
      title: "Systems per Life Cycle ",
    },
  });

  lifeCycleDonutChartComponent.mapState(({ dashboard }) => {
    const allItems = getAllReportingItems(dashboard);
    const { entries } = getLifeCycleData(allItems);

    return {
      title: withTotalInTitle("Systems per Life Cycle", allItems.length),
      icon: "DonutLarge",
      ...DONUT_CARD_LAYOUT,
      data: entries.map((entry) => ({
        label: entry.label,
        value: entry.count,
        color: entry.color,
      })),
    };
  });
}
